import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Navigation, Loader2, MapPin, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SearchForm = ({ onSearch, onGeolocate, isLoading }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isGeolocating, setIsGeolocating] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  // Fetch suggestions with debounce
  const fetchSuggestions = useCallback(async (searchQuery) => {
    if (searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoadingSuggestions(true);
    try {
      const response = await axios.get(`${API}/autocomplete`, {
        params: { q: searchQuery, limit: 5 }
      });
      setSuggestions(response.data || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Autocomplete error:', error);
      setSuggestions([]);
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, []);

  // Debounced input handler
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length >= 2) {
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(query);
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, fetchSuggestions]);

  // Close suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim().length >= 2) {
      setShowSuggestions(false);
      onSearch(query.trim());
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion.label);
    setShowSuggestions(false);
    setSuggestions([]);
    
    // If we have coordinates, use geolocation search for more accurate results
    if (suggestion.latitude && suggestion.longitude) {
      onGeolocate(suggestion.latitude, suggestion.longitude);
    } else {
      onSearch(suggestion.city || suggestion.label);
    }
  };

  const handleClearInput = () => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleGeolocate = async () => {
    if (!navigator.geolocation) {
      alert('La géolocalisation n\'est pas supportée par votre navigateur');
      return;
    }

    setIsGeolocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        onGeolocate(latitude, longitude);
        setIsGeolocating(false);
        setQuery('');
        setShowSuggestions(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        alert('Impossible d\'obtenir votre position. Vérifiez vos autorisations.');
        setIsGeolocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <form 
      onSubmit={handleSubmit} 
      data-testid="search-form"
      className="bg-white dark:bg-gray-900 p-6 border-b-2 border-black dark:border-gray-700"
    >
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder="Ville, adresse ou code postal..."
          data-testid="search-input"
          className="w-full p-4 pr-24 text-lg font-bold border-2 border-black dark:border-gray-600 rounded-none placeholder:text-gray-400 placeholder:font-normal bg-white dark:bg-gray-800 text-black dark:text-white focus:bg-[#FFFBEB] dark:focus:bg-gray-700 transition-colors"
          disabled={isLoading || isGeolocating}
          autoComplete="off"
        />
        
        {/* Clear button */}
        {query && (
          <button
            type="button"
            onClick={handleClearInput}
            data-testid="clear-input-button"
            className="absolute right-14 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-black dark:hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
        
        {/* Geolocation button */}
        <button
          type="button"
          onClick={handleGeolocate}
          disabled={isLoading || isGeolocating}
          data-testid="geolocation-button"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black dark:bg-white text-white dark:text-black hover:bg-[#FF4500] dark:hover:bg-[#FF4500] dark:hover:text-white transition-colors disabled:bg-gray-400"
          title="Utiliser ma position"
        >
          {isGeolocating ? (
            <Loader2 className="w-5 h-5 spinner" />
          ) : (
            <Navigation className="w-5 h-5" />
          )}
        </button>

        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div 
            ref={suggestionsRef}
            data-testid="suggestions-dropdown"
            className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 shadow-hard max-h-64 overflow-y-auto"
          >
            {suggestions.map((suggestion, index) => (
              <button
                key={`${suggestion.postcode}-${index}`}
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                data-testid={`suggestion-${index}`}
                className="w-full px-4 py-3 text-left hover:bg-[#FFFBEB] dark:hover:bg-gray-700 transition-colors border-b border-gray-200 dark:border-gray-700 last:border-b-0 flex items-start gap-3"
              >
                <MapPin className="w-4 h-4 text-[#FF4500] flex-shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-black dark:text-white truncate">{suggestion.label}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{suggestion.context}</p>
                </div>
                <span className="text-xs font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 text-gray-600 dark:text-gray-300 flex-shrink-0">
                  {suggestion.postcode}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Loading indicator for suggestions */}
        {isLoadingSuggestions && query.length >= 2 && (
          <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 p-4 flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
            <Loader2 className="w-4 h-4 spinner" />
            <span className="text-sm">Recherche...</span>
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={isLoading || query.trim().length < 2}
        data-testid="search-submit-button"
        className="w-full mt-4 p-4 bg-black dark:bg-white text-white dark:text-black text-xl font-heading font-bold uppercase tracking-widest hover:bg-[#FF4500] dark:hover:bg-[#FF4500] dark:hover:text-white transition-colors shadow-hard-interactive disabled:bg-gray-400 disabled:shadow-none disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 spinner" />
            Recherche...
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <Search className="w-5 h-5" />
            Rechercher
          </span>
        )}
      </button>
    </form>
  );
};
